/**
 * Import time entries from a Clockify "Detailed Report" (.xlsx / .csv).
 *
 * The file is parsed server-side and every row is created through the same
 * service the "Log time" button uses, so imported rows show up in the existing
 * list as normal Pending entries — this modal only picks the file and reports
 * the summary it gets back.
 *
 * That summary can include employees the import had to create, because the file
 * named people who aren't set up here yet. They are the one result worth
 * reading closely — an account now exists for each of them — so they get the
 * same prominent callout the unmatched warning used to own.
 */
import React, { useRef, useState } from "react";
import { toast } from "../../utils/toast";
import { Upload, FileSpreadsheet, X } from "lucide-react";
import { wsApi } from "./wsApi";
import { T, ModalOverlay } from "../../components/em/ui";

const ACCEPT = ".xlsx,.csv";
const isSupported = (name = "") => /\.(xlsx|csv)$/i.test(name);

export default function ImportTimesheetModal({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const pick = (f) => {
    if (!f) return;
    if (!isSupported(f.name)) return toast.error("Pick a .xlsx or .csv export");
    setResult(null);
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    pick(e.dataTransfer.files?.[0]);
  };

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const summary = await wsApi.importTimeEntries(file);
      setResult(summary);
      toast.success(`${summary.inserted} imported`);
      // Refresh the list behind the modal so the new entries are already there.
      onImported?.();
    } catch (e) {
      toast.error(e?.response?.data?.error || "Could not import the file");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalOverlay onClose={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 460,
          background: "var(--em-bg-card)",
          borderRadius: 14,
          boxShadow: T.shadowMd,
          padding: 20,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <span style={{ font: `700 15px ${T.font}`, color: T.fg1 }}>Import from Clockify</span>
          <button onClick={onClose} style={{ background: "none", border: 0, cursor: "pointer", color: T.fg5, display: "flex" }}>
            <X size={18} />
          </button>
        </div>

        {result ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ font: `600 13px ${T.font}`, color: T.fg1 }}>
              {result.inserted} imported
              {result.createdEmployees?.length
                ? `, ${result.createdEmployees.length} employee${result.createdEmployees.length === 1 ? "" : "s"} added`
                : ""}
              {result.skippedDuplicate ? `, ${result.skippedDuplicate} duplicates skipped` : ""}
              {result.skippedUnmatched ? `, ${result.skippedUnmatched} unmatched skipped` : ""}
              {result.errorCount ? `, ${result.errorCount} errors` : ""}
            </div>
            <div style={{ font: `400 11.5px ${T.font}`, color: T.fg7 }}>
              {result.total} rows read. Imported entries are Pending — filter the list by
              Pending to review them.
            </div>
            {!!result.createdEmployees?.length && (
              <div
                style={{
                  font: `500 11.5px ${T.font}`,
                  color: "#c2410c",
                  background: "#fff7ed",
                  border: "1px solid #fed7aa",
                  borderRadius: 8,
                  padding: "8px 10px",
                }}
              >
                {result.createdEmployees.length} employee
                {result.createdEmployees.length === 1 ? " was" : "s were"} not in the system
                and {result.createdEmployees.length === 1 ? "has" : "have"} been added from
                the file, with their time:{" "}
                {result.createdEmployees
                  .slice(0, 4)
                  .map((e) => `${e.name} (${e.employeeid})`)
                  .join(", ")}
                {result.createdEmployees.length > 4
                  ? ` +${result.createdEmployees.length - 4} more`
                  : ""}
                . {result.createdEmployees.length === 1 ? "The record is" : "The records are"}{" "}
                marked Inactive and hold only a name and email — fill{" "}
                {result.createdEmployees.length === 1 ? "it" : "them"} in on the Team page, and
                send an invite from there when they need to log in.
              </div>
            )}
            {!!result.unmatchedEmployees?.length && (
              <div
                style={{
                  font: `500 11.5px ${T.font}`,
                  color: "#c2410c",
                  background: "#fff7ed",
                  border: "1px solid #fed7aa",
                  borderRadius: 8,
                  padding: "8px 10px",
                }}
              >
                {result.skippedUnmatched} row
                {result.skippedUnmatched === 1 ? " was" : "s were"} skipped — no employee here
                has the email {result.unmatchedEmployees.slice(0, 4).join(", ")}
                {result.unmatchedEmployees.length > 4
                  ? ` +${result.unmatchedEmployees.length - 4} more`
                  : ""}
                , and no record could be created for{" "}
                {result.unmatchedEmployees.length === 1 ? "it" : "them"} either. The rows below
                say why.
              </div>
            )}
            {!!result.createdProjects?.length && (
              <div style={{ font: `400 11.5px ${T.font}`, color: T.fg7 }}>
                Created {result.createdProjects.length} new project
                {result.createdProjects.length === 1 ? "" : "s"}:{" "}
                {result.createdProjects.slice(0, 5).join(", ")}
                {result.createdProjects.length > 5
                  ? ` +${result.createdProjects.length - 5} more`
                  : ""}
                . You and the employees on those rows have been added to{" "}
                {result.createdProjects.length === 1 ? "it" : "them"}.
              </div>
            )}
            {!!result.errors?.length && (
              <div
                style={{
                  maxHeight: 160,
                  overflowY: "auto",
                  border: `1px solid ${T.border}`,
                  borderRadius: 8,
                  padding: "8px 10px",
                  background: T.bgMuted,
                }}
              >
                {result.errors.map((e) => (
                  <div key={e.row} style={{ font: `400 11.5px ${T.font}`, color: T.fg3, marginBottom: 3 }}>
                    Row {e.row}: {e.error}
                  </div>
                ))}
                {result.errorCount > result.errors.length && (
                  <div style={{ font: `500 11.5px ${T.font}`, color: T.fg7 }}>
                    …and {result.errorCount - result.errors.length} more
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <>
            <div
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              style={{
                border: `1.5px dashed ${dragging ? T.orange : T.border}`,
                borderRadius: 12,
                background: dragging ? "#fff7ed" : T.bgMuted,
                padding: "26px 16px",
                textAlign: "center",
                cursor: "pointer",
              }}
            >
              {file ? <FileSpreadsheet size={22} color={T.orange} /> : <Upload size={22} color={T.fg5} />}
              <div style={{ font: `600 13px ${T.font}`, color: T.fg1, marginTop: 8 }}>
                {file ? file.name : "Drop the Clockify export here"}
              </div>
              <div style={{ font: `400 11.5px ${T.font}`, color: T.fg7, marginTop: 3 }}>
                {file ? "Click to choose a different file" : "or click to choose a .xlsx / .csv file"}
              </div>
              <input
                ref={inputRef}
                type="file"
                accept={ACCEPT}
                onChange={(e) => pick(e.target.files?.[0])}
                style={{ display: "none" }}
              />
            </div>
            <div style={{ font: `400 11.5px ${T.font}`, color: T.fg7, marginTop: 10 }}>
              Rows are matched to employees by email — the entry then carries that employee's
              name here, whatever the file calls them. An email that matches no one gets an
              employee added from the file, marked Inactive with no password, so their time
              still imports; you'll get the list to complete on the Team page. Projects the
              file names but we don't have yet are created, with you and the employees on those
              rows added to them. Re-importing the same file adds no duplicates.
            </div>
          </>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
          <button
            onClick={onClose}
            disabled={busy}
            style={{
              padding: "8px 14px",
              borderRadius: T.rMd,
              border: `1px solid ${T.border}`,
              background: "var(--em-bg-card)",
              color: T.fg2,
              font: `600 13px ${T.font}`,
              cursor: "pointer",
            }}
          >
            {result ? "Done" : "Cancel"}
          </button>
          {!result && (
            <button
              onClick={upload}
              disabled={busy || !file}
              style={{
                padding: "8px 16px",
                borderRadius: T.rMd,
                border: 0,
                background: T.gradient,
                color: "#fff",
                font: `600 13px ${T.font}`,
                cursor: busy || !file ? "not-allowed" : "pointer",
                opacity: busy || !file ? 0.6 : 1,
                boxShadow: T.shadowBrand,
              }}
            >
              {busy ? "Importing…" : "Import"}
            </button>
          )}
        </div>
      </div>
    </ModalOverlay>
  );
}
